import { clientService } from '@hixme/api'

export type PartnerBenefitConfig = {
  Enabled?: boolean
  PartnerType?: string
}

export type CompanyWithPartnerBenefitConfig = {
  PartnerBenefitConfig?: PartnerBenefitConfig
}

type DynamoClient = {
  Id?: string
  PartnerBenefitConfig?: PartnerBenefitConfig
}

let dynamoClientsRequest: Promise<DynamoClient[]> | null = null

export function isNoyoPartnerConfig(
  config: PartnerBenefitConfig | undefined
): boolean {
  if (!config?.Enabled) return false
  return (config.PartnerType ?? '').toLowerCase() === 'noyo'
}

/** Test-only reset for the cached clients list. */
export function resetDynamoClientsCache() {
  dynamoClientsRequest = null
}

async function loadDynamoClients(): Promise<DynamoClient[]> {
  if (!dynamoClientsRequest) {
    const request = clientService({ route: 'clients' })
      .then((response: { Clients?: DynamoClient[] }) => response?.Clients ?? [])
      .catch((error: unknown): never => {
        dynamoClientsRequest = null
        throw error
      })
    dynamoClientsRequest = request
  }
  const request = dynamoClientsRequest
  if (!request) {
    throw new Error('Dynamo clients request was not started')
  }
  return request
}

/**
 * The company object in the admin Redux store comes from
 * `/api/v1/members/me/administered-customers` and does not include
 * `PartnerBenefitConfig`. When the store record has the field, use it.
 * Otherwise load the Dynamo clients list (the same `clients` route person
 * search uses) and match `ClientPublicKey`. If that lookup fails, return
 * true so the caller still asks partner-benefit-graph, which is the
 * authority for whether the employer is Noyo-enabled.
 */
export async function isEmployerNoyoIntegrated(params: {
  company?: CompanyWithPartnerBenefitConfig
  clientPublicKey?: string
}): Promise<boolean> {
  if (params.company?.PartnerBenefitConfig) {
    return isNoyoPartnerConfig(params.company.PartnerBenefitConfig)
  }
  if (!params.clientPublicKey) return false
  try {
    const clients = await loadDynamoClients()
    const match = clients.find((client) => client.Id === params.clientPublicKey)
    if (!match?.PartnerBenefitConfig) return true
    return isNoyoPartnerConfig(match.PartnerBenefitConfig)
  } catch {
    return true
  }
}

export type NoyoSyncFailure = 'not-enabled' | 'in-progress' | 'failed'

export function classifyNoyoSyncError(error: unknown): NoyoSyncFailure {
  const response = (error as { response?: { data?: unknown } })?.response?.data
  const data =
    response && typeof response === 'object'
      ? (response as { type?: string; message?: unknown })
      : undefined
  const message = [
    typeof data?.message === 'string' ? data.message : '',
    error instanceof Error ? error.message : '',
  ]
    .filter((part) => part.length > 0)
    .join(' ')
  if (
    data?.type === 'CLIENT_SYNC_NOT_ENABLED_ERROR' ||
    /not enabled for noyo/i.test(message)
  ) {
    return 'not-enabled'
  }
  if (
    data?.type === 'SYNC_TASK_ALREADY_EXISTS_ERROR' ||
    /already an active task/i.test(message)
  ) {
    return 'in-progress'
  }
  return 'failed'
}
